"use client";
import type { ChallengeObj, GameState, Solution } from "@/interfaces/vidyaGrid";
import { cn } from "@/lib/utils";
import { MdOutlineQuestionMark } from "react-icons/md";
import LinesEllipsis from "react-lines-ellipsis";

interface GuessCellProps {
  colCha: ChallengeObj;
  rowCha: ChallengeObj;
  getSolution: (
    colCha: ChallengeObj,
    rowCha: ChallengeObj
  ) => Solution | undefined;
  attemptGuess: (colCha: ChallengeObj, rowCha: ChallengeObj) => void;
  gameState: GameState;
}

export default function GuessCell(props: GuessCellProps) {
  const { colCha, rowCha, getSolution, attemptGuess, gameState } = props;

  const solution = getSolution(colCha, rowCha);

  const commonClasses = cn([
    "rounded-md",
    "h-[87px] w-[64px]",
    "sm:h-[93px] sm:w-[70px]",
    "2xl:h-[176px] 2xl:w-[132px]",
  ]);

  return (
    <div
      data-testid="guess-cell"
      className={cn(
        [
          commonClasses,
          "bg-blue-gray-500",
          "text-center",
          "grid",
          "justify-center",
          "content-center",
        ],
        {
          "transition cursor-pointer hover:scale-105":
            !solution && gameState === "playing",
        }
      )}
      onClick={() => {
        if (solution || gameState !== "playing") return;
        attemptGuess(colCha, rowCha);
      }}
    >
      {solution && (
        <div
          style={{ ["--image-url" as string]: `url(${solution.url})` }}
          className={cn([
            commonClasses,
            "bg-contain bg-[image:var(--image-url)]",
            "[&_*]:opacity-0 [&:hover_*]:opacity-100",
          ])}
        >
          <div
            className={cn([
              commonClasses,
              "transition",
              "content-center",
              "bg-black/60",
              "px-1",
              "overflow-hidden",
            ])}
          >
            <span className="text-center font-semibold text-xs 2xl:text-lg">
              <LinesEllipsis text={solution.name} maxLine={4} />
            </span>
          </div>
        </div>
      )}
      {!solution && <MdOutlineQuestionMark className="w-8 h-8 text-white" />}
    </div>
  );
}
